import { useState, useCallback, useEffect } from "react";
import { useOrgContext } from "../context/OrgContext";
import { useLocale } from "../context/LanguageContext";
import {
  RosterStatsBar,
  SeatCapacityBanner,
  RosterUploadZone,
  RosterDryRunPreview,
  RosterTable,
  ImportResultsBanner,
  DownloadTemplateButton,
  ExportRosterButton,
  parseCSVForPreview,
  useOrganizationRoster,
  useOrganizationRosterStats,
  useOrganizationSeatUsage,
  useBulkImportUsers,
  useSendRosterInvites,
} from "../features/roster";
import type {
  BulkImportReport,
  SendInvitesReport,
  RosterEnrollmentStatus,
  CsvParseResult,
} from "../features/roster";

const PAGE_SIZE = 10;

export function RosterPage() {
  const { currentOrgId, currentOrg } = useOrgContext();
  const { t } = useLocale();

  // ─── Pagination & Filter State ──────────────────────────────────────────
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] =
    useState<RosterEnrollmentStatus | null>(null);

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0); // Reset to first page on search change
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when status filter changes
  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  // ─── Data Fetching ──────────────────────────────────────────────────────
  const { data: rosterData, loading: rosterLoading, refetch: refetchRoster } =
    useOrganizationRoster({
      organizationId: currentOrgId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      search: debouncedSearch || undefined,
      status: statusFilter ?? undefined,
    });

  const { data: statsData, loading: statsLoading, refetch: refetchStats } =
    useOrganizationRosterStats(currentOrgId);

  const { data: seatData, refetch: refetchSeatUsage } =
    useOrganizationSeatUsage(currentOrgId);

  const seatUsage = seatData?.organization?.seatUsage ?? null;

  // ─── Upload/Import State ────────────────────────────────────────────────
  const [dryRunReport, setDryRunReport] = useState<BulkImportReport | null>(
    null
  );
  const [importResult, setImportResult] = useState<BulkImportReport | null>(
    null
  );
  const [seatLimitError, setSeatLimitError] = useState<string | null>(null);
  const [inviteResult, setInviteResult] = useState<SendInvitesReport | null>(
    null
  );
  const [selectedFileName, setSelectedFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<CsvParseResult | null>(null);

  const [bulkImport, { loading: isImporting }] = useBulkImportUsers();
  const [sendInvites, { loading: isSendingInvites }] = useSendRosterInvites();

  // ─── Handlers ───────────────────────────────────────────────────────────

  /** Step 1: User drops a file → parse preview + run dry-run */
  const handleFileSelected = useCallback(
    async (file: File) => {
      if (!currentOrgId) return;

      setSelectedFile(file);
      setSelectedFileName(file.name);
      setDryRunReport(null);
      setImportResult(null);
      setSeatLimitError(null);
      setCsvPreview(null);

      // Parse CSV locally for data preview (only for .csv files)
      const isCsv = file.name.toLowerCase().endsWith(".csv");
      if (isCsv) {
        try {
          const preview = await parseCSVForPreview(file, 50);
          setCsvPreview(preview);
        } catch {
          // Non-critical: preview is optional, dry-run still runs
          console.warn("[RosterPage] CSV preview parse failed");
        }
      }

      try {
        const { data } = await bulkImport({
          variables: {
            organizationId: currentOrgId,
            file,
            input: {
              mode: "ADD_ONLY",
              enrollExistingUsers: true,
              updateExistingMembers: false,
              dryRun: true,
            },
          },
        });

        if (data?.bulkImportUsers) {
          setDryRunReport(data.bulkImportUsers);
        }
      } catch (err) {
        // Check for seat limit (RESOURCE_EXHAUSTED) error
        const gqlErr = (err as { graphQLErrors?: Array<{ extensions?: { code?: string }; message?: string }> })
          .graphQLErrors?.[0];
        if (gqlErr?.extensions?.code === "RESOURCE_EXHAUSTED") {
          setSeatLimitError(t("seats.limitExceededGeneric"));
          void refetchSeatUsage();
          return;
        }

        console.error("[RosterPage] Dry-run failed:", err);
        setDryRunReport({
          successCount: 0,
          skippedCount: 0,
          errorCount: 1,
          errors: [
            {
              row: 0,
              error: t("roster.dryRunFailed"),
            },
          ],
          warnings: [],
          skippedRows: [],
        });
      }
    },
    [currentOrgId, bulkImport, refetchSeatUsage, t]
  );

  /** Step 2: User confirms → run real import */
  const handleConfirmImport = useCallback(async () => {
    if (!currentOrgId || !selectedFile) return;

    try {
      const { data } = await bulkImport({
        variables: {
          organizationId: currentOrgId,
          file: selectedFile,
          input: {
            mode: "ADD_ONLY",
            enrollExistingUsers: true,
            updateExistingMembers: false,
            dryRun: false,
          },
        },
      });

      if (data?.bulkImportUsers) {
        setImportResult(data.bulkImportUsers);
        setDryRunReport(null);
        setSelectedFile(null);
        setCsvPreview(null);
        // Refresh roster data, stats, and seat usage
        void refetchRoster();
        void refetchStats();
        void refetchSeatUsage();
      }
    } catch (err) {
      // Check for seat limit (RESOURCE_EXHAUSTED) error
      const gqlErr = (err as { graphQLErrors?: Array<{ extensions?: { code?: string }; message?: string }> })
        .graphQLErrors?.[0];
      if (gqlErr?.extensions?.code === "RESOURCE_EXHAUSTED") {
        setSeatLimitError(t("seats.limitExceededGeneric"));
        setDryRunReport(null);
        void refetchSeatUsage();
        return;
      }

      console.error("[RosterPage] Import failed:", err);
      setImportResult({
        successCount: 0,
        skippedCount: 0,
        errorCount: 1,
        errors: [
          {
            row: 0,
            error: t("roster.importFailed"),
          },
        ],
        warnings: [],
        skippedRows: [],
      });
      setDryRunReport(null);
    }
  }, [currentOrgId, selectedFile, bulkImport, refetchRoster, refetchStats, refetchSeatUsage, t]);

  /** Cancel dry-run preview */
  const handleCancelDryRun = useCallback(() => {
    setDryRunReport(null);
    setSelectedFile(null);
    setSelectedFileName("");
    setCsvPreview(null);
  }, []);

  /** Send invite emails to selected roster entries */
  const handleSendInvites = useCallback(
    async (emails: string[]) => {
      if (!currentOrgId) return;

      try {
        const { data } = await sendInvites({
          variables: { organizationId: currentOrgId, emails },
        });

        if (data?.sendRosterInvites) {
          setInviteResult(data.sendRosterInvites);
          void refetchRoster();
          void refetchStats();
        }
      } catch (err) {
        console.error("[RosterPage] Send invites failed:", err);
        // Show error feedback to user
        setInviteResult({
          sent: 0,
          alreadyRegistered: 0,
          failed: emails.length,
        });
      }
    },
    [currentOrgId, sendInvites, refetchRoster, refetchStats]
  );

  // ─── Guard: no org selected ─────────────────────────────────────────────
  if (!currentOrgId || !currentOrg) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <p className="text-lg font-medium text-foreground">
          {t("common.selectOrg")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("common.selectOrgHint")}
        </p>
      </div>
    );
  }

  const roster = rosterData?.organizationRoster;
  const stats = statsData?.organizationRosterStats;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("roster.title")}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t("roster.description", { orgName: currentOrg.name })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <ExportRosterButton
            organizationId={currentOrgId}
            orgSlug={currentOrg.slug ?? currentOrg.name}
            statusFilter={statusFilter}
          />
          <DownloadTemplateButton />
        </div>
      </div>

      {/* Stats Bar */}
      <RosterStatsBar stats={stats} seatUsage={seatUsage} isLoading={statsLoading} />

      {/* Seat Capacity Banner */}
      {seatUsage && (
        <SeatCapacityBanner
          seatUsage={seatUsage}
          organizationName={currentOrg.name}
          showUpgrade
        />
      )}

      {/* Seat Limit Error Modal */}
      {seatLimitError && (
        <>
          <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setSeatLimitError(null)} />
          <div className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg border border-destructive/30 bg-card p-6 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-destructive/10 p-2">
                <svg className="h-5 w-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-foreground">{t("seats.limitExceeded")}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{seatLimitError}</p>
                {seatUsage && seatUsage.limit != null && (
                  <div className="mt-3 rounded-md bg-muted p-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("seats.currentUsage")}</span>
                      <span className="font-medium text-foreground">{seatUsage.used} / {seatUsage.limit}</span>
                    </div>
                    <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-destructive transition-all"
                        style={{ width: `${Math.min(seatUsage.percentage ?? 0, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  <a
                    href={`mailto:sales@kommit.app?subject=Seat%20Limit%20Upgrade%20for%20${encodeURIComponent(currentOrg.name)}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                  >
                    {t("common.contactSales")}
                  </a>
                  <button
                    type="button"
                    onClick={() => setSeatLimitError(null)}
                    className="rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    {t("common.dismiss")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Import Result Banner */}
      {importResult && (
        <ImportResultsBanner
          report={importResult}
          onDismiss={() => setImportResult(null)}
        />
      )}

      {/* Invite Result Banner */}
      {inviteResult && (
        <div
          className={`flex items-center gap-3 rounded-lg border p-4 text-sm ${
            inviteResult.sent > 0
              ? "border-success/30 bg-success/5"
              : inviteResult.failed > 0
                ? "border-destructive/30 bg-destructive/5"
                : "border-warning/30 bg-warning/5"
          }`}
        >
          <span className="text-foreground">
            {inviteResult.sent > 0 && (
              <span className="font-medium">
                {t("roster.invitesSent", { count: inviteResult.sent })}
              </span>
            )}
            {inviteResult.alreadyRegistered > 0 && (
              <span className="text-muted-foreground">
                {inviteResult.sent > 0 ? " · " : ""}
                {t("roster.alreadyRegistered", { count: inviteResult.alreadyRegistered })}
              </span>
            )}
            {inviteResult.failed > 0 && (
              <span className="text-destructive">
                {inviteResult.sent > 0 || inviteResult.alreadyRegistered > 0
                  ? " · "
                  : ""}
                {t("roster.invitesFailed", { count: inviteResult.failed })}
              </span>
            )}
            {inviteResult.sent === 0 &&
              inviteResult.alreadyRegistered === 0 &&
              inviteResult.failed === 0 && (
                <span className="text-muted-foreground">
                  {t("roster.noInvitesSent")}
                </span>
              )}
          </span>
          <button
            type="button"
            onClick={() => setInviteResult(null)}
            className="ml-auto text-muted-foreground hover:text-foreground"
          >
            ✕
          </button>
        </div>
      )}

      {/* Upload or Dry-Run Preview */}
      {dryRunReport ? (
        <RosterDryRunPreview
          report={dryRunReport}
          fileName={selectedFileName}
          csvPreview={csvPreview}
          onConfirm={handleConfirmImport}
          onCancel={handleCancelDryRun}
          isImporting={isImporting}
        />
      ) : (
        <RosterUploadZone
          onFileSelected={handleFileSelected}
          isUploading={isImporting}
        />
      )}

      {/* Roster Data Table */}
      <RosterTable
        items={roster?.items ?? []}
        total={roster?.total ?? 0}
        hasMore={roster?.hasMore ?? false}
        isLoading={rosterLoading}
        page={page}
        pageSize={PAGE_SIZE}
        search={search}
        statusFilter={statusFilter}
        onSearchChange={setSearch}
        onStatusFilterChange={setStatusFilter}
        onPageChange={setPage}
        onSendInvites={handleSendInvites}
        isSendingInvites={isSendingInvites}
      />
    </div>
  );
}
